import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { KanbanCandidate } from "@/components/jobs/kanban/types/index";
import { useState, useEffect, useRef } from "react";

interface UseKanbanMatchesProps {
	jobId?: string;
	candidateId?: string;
	showAllMatches?: boolean;
}

export const useKanbanMatches = ({ jobId, candidateId, showAllMatches = false }: UseKanbanMatchesProps) => {
	const [loading, setLoading] = useState(true);
	const [matches, setMatches] = useState<KanbanCandidate[]>([]);
	const intervalRef = useRef<NodeJS.Timeout | null>(null);

	const checkAnalysisStatus = async (matchIds: string[]) => {
		if (matchIds.length === 0) return;

		try {
			const { data: statusData, error } = await supabase
				.from('matches')
				.select('id, ai_analysis')
				.in('id', matchIds);

			if (error) {
				console.error('Error checking analysis status:', error);
				return;
			}

			if (statusData) {
				const updatedStatuses = new Map(statusData.map(item => [item.id, item.ai_analysis !== null]));

				setMatches(prevMatches => {
					let analysisStillInProgress = false;
					const updatedMatches = prevMatches.map(match => {
						if (updatedStatuses.has(match.id)) {
							const analysisComplete = updatedStatuses.get(match.id);
							const newAnalysisInProgress = !analysisComplete;
							if (newAnalysisInProgress) {
								analysisStillInProgress = true;
							}
							return { ...match, analysisInProgress: newAnalysisInProgress };
						}
						if (match.analysisInProgress) {
							analysisStillInProgress = true;
						}
						return match;
					});

					if (!analysisStillInProgress && intervalRef.current) {
						clearInterval(intervalRef.current);
						intervalRef.current = null;
						console.log("Analysis polling stopped.");
					}

					return updatedMatches;
				});
			}
		} catch (error) {
			console.error('Failed to check analysis status:', error);
		}
	};

	const fetchMatches = async () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
		setLoading(true);
		try {
			let query = supabase
				.from('matches')
				.select(`
					id,
					candidate_id,
					job_id,
					score,
					status,
					created_at,
					candidates(
						id,
						name,
						skills,
						email,
						function
					),
					jobs(
						id,
						title,
						skills,
						company_id,
						companies(name)
					),
					ai_analysis
				`);

			if (jobId) {
				query = query.eq('job_id', jobId);
			}
			if (candidateId) {
				query = query.eq('candidate_id', candidateId);
			}

			const { data: matchesData, error } = await query;

			if (error) throw error;

			const transformedMatches: KanbanCandidate[] = matchesData.map(match => ({
				id: match.id,
				candidateId: match.candidate_id,
				jobId: match.job_id,
				stage: match.status || 'matched',
				email: match.candidates?.email || '',
				score: match.score || 0,
				
				createdAt: match.created_at,
				date: new Date(match.created_at || '').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
				analysisInProgress: (match.ai_analysis === null),

				skills: (jobId)? match.candidates?.skills : match.jobs?.skills || [],
				name: (candidateId)? match.jobs?.title : match.candidates.name || 'Unknown',
				jobTitle: (jobId)? '' : match.jobs?.title || '',
				position: jobId ? (match.candidates?.function || '') : '',
				company: candidateId ? match.jobs?.companies?.name  : '',

			}));

			setMatches(transformedMatches);
		} catch (error) {
			console.error('Error fetching matches:', error);
			toast({
				title: "Error",
				description: "Failed to load matches",
				variant: "destructive",
			});
			setMatches([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchMatches();
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
			}
		};
	}, [jobId, candidateId, showAllMatches]);

	useEffect(() => {
		const matchesToCheck = matches.filter(m => m.analysisInProgress).map(m => m.id);

		if (matchesToCheck.length > 0 && !intervalRef.current) {
			console.log("Starting analysis polling for matches:", matchesToCheck);
			intervalRef.current = setInterval(() => {
				checkAnalysisStatus(matchesToCheck);
			}, 30000);
		} else if (matchesToCheck.length === 0 && intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
			console.log("Analysis polling stopped (no matches to check).");
		}

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
				intervalRef.current = null;
				console.log("Analysis polling stopped (cleanup).");
			}
		};
	}, [matches]);

	const handleStageChange = async (matchId: string, newStage: string) => {
		try {
			const { error } = await supabase
				.from('matches')
				.update({ status: newStage })
				.eq('id', matchId);

			if (error) throw error;

			toast({
				title: "Success",
				description: `Candidate moved to ${newStage} stage`,
			});

			fetchMatches();
			return true;
		} catch (error) {
			console.error('Error updating match stage:', error);
			toast({
				title: "Error",
				description: "Failed to update match stage",
				variant: "destructive",
			});
			return false;
		}
	};

	return {
		loading,
		matches,
		handleStageChange,
		refreshMatches: fetchMatches
	};
};
